import React from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
} from '@mui/material';

import { RootState, selectApplications } from '../store';
import { useTranslation } from '../i18n/useTranslation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ScoreIcon from '@mui/icons-material/Score';
import CreditScoreGauge from './CreditScoreGauge';

const ApplicationDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const applications = useSelector(selectApplications);
  const application = Array.isArray(applications) ? applications.find(app => app.id === id) : undefined;

  if (!application) {
    return <Typography variant="h5">{t('applicationDetail.applicationNotFound')}</Typography>;
  }
  
  const getStatusChip = (status: 'approved' | 'rejected' | 'pending' | 'disbursed') => {
    let color: 'success' | 'error' | 'warning' = 'warning';
    if (status === 'approved' || status === 'disbursed') color = 'success';
    else if (status === 'rejected') color = 'error';
    return <Chip label={status.toUpperCase()} color={color} sx={{ fontWeight: 'bold' }} />;
  };

  return (
    <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
        <Button
            component={Link}
            to="/status"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
        >
            Back to Applications
        </Button>
      <Paper elevation={3} sx={{ p: 3, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="div">
              {application.applicantName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Application ID: {application.id}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {getStatusChip(application.status)}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Card elevation={2} sx={{ flex: '1 1 400px', minWidth: '350px' }}>
                <CardHeader title={t('applicationDetail.applicantInformation')} avatar={<PersonIcon/>}/>
                <CardContent>
                    <Typography><strong>Email:</strong> {application.email || 'N/A'}</Typography>
                    <Typography><strong>Phone:</strong> {application.phone || 'N/A'}</Typography>
                    <Typography><strong>National ID:</strong> {application.nationalId || 'N/A'}</Typography>
                    <Typography><strong>Age:</strong> {application.age || 'N/A'}</Typography>
                    {application.customerType === 'sme' && (
                        <>
                            <Divider sx={{ my: 1 }}/>
                            <Typography><strong>Business Name:</strong> {application.businessName || 'N/A'}</Typography>
                            <Typography><strong>Business Type:</strong> {application.businessType || 'N/A'}</Typography>
                            <Typography><strong>Years in Business:</strong> {application.yearsInBusiness || 'N/A'}</Typography>
                        </>
                    )}
                </CardContent>
            </Card>
            <Card elevation={2} sx={{ flex: '1 1 400px', minWidth: '350px' }}>
                <CardHeader title="Loan & Employment" avatar={<AttachMoneyIcon/>}/>
                <CardContent>
                    <Typography><strong>Monthly Income:</strong> {application.monthlyIncome ? `${application.monthlyIncome.toLocaleString('en-ZW')} ZiG` : 'N/A'}</Typography>
                    <Typography><strong>Requested Amount:</strong> {application.requestedAmount ? `${application.requestedAmount.toLocaleString('en-ZW')} ZiG` : 'N/A'}</Typography>
                    <Typography><strong>Employment Status:</strong> {application.employmentStatus || 'N/A'}</Typography>
                    <Typography><strong>Employment Duration:</strong> {application.employmentDuration ? `${application.employmentDuration} years` : 'N/A'}</Typography>
                    <Typography><strong>Existing Loans:</strong> {application.existingLoans || 'N/A'}</Typography>
                </CardContent>
            </Card>
          </Box>
          
          <Card elevation={2}>
            <CardHeader title={t('applicationDetail.uploadedDocuments')} />
            <CardContent>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 2
              }}>
                {(application.documents ? Object.keys(application.documents) : [
                  'nationalIdFront', 'nationalIdBack', 'addressProof', 'incomeProof', 'bankStatement'
                ]).map((docKey, index) => {
                  const docFile = application.documents
                    ? (application.documents as Record<string, File | undefined>)[docKey]
                    : undefined;
                  const label = docKey
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (c) => c.toUpperCase());
                  const fallbackImages = ['/logo512.svg', '/logo192.svg'];
                  const imageSrc = fallbackImages[index % fallbackImages.length];
                  return (
                    <Card key={docKey} variant="outlined" sx={{ overflow: 'hidden' }}>
                      {/* For now we show a dummy image when real file preview is unavailable */}
                      <CardMedia
                        component="img"
                        height="120"
                        image={imageSrc}
                        alt={label}
                        sx={{ objectFit: 'cover', bgcolor: 'background.default' }}
                      />
                      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>{label}</Typography>
                        <Chip size="small" label={docFile ? 'Submitted' : 'Placeholder'} color={docFile ? 'success' : 'warning'} />
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
          
          <Card elevation={2}>
              <CardHeader title={t('applicationDetail.scoringResult')} avatar={<ScoreIcon/>}/>
              <CardContent>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', textAlign: 'center' }}>
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                          <Typography variant="h6" gutterBottom>Credit Score</Typography>
                          <CreditScoreGauge score={application.creditScore ?? 0} />
                      </Box>
                      <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                          <Typography variant="h6" gutterBottom>Interest Rate</Typography>
                          <Typography variant="h3" color="text.primary">{application.interestRate ? `${application.interestRate}%` : 'N/A'}</Typography>
                      </Box>
                       <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                          <Typography variant="h6" gutterBottom>Status</Typography>
                          {getStatusChip(application.status)}
                      </Box>
                  </Box>
              </CardContent>
          </Card>
          
          <Card elevation={2}>
            <CardHeader title="Lifecycle & Repayments" />
            <CardContent>
              <Button component={Link} to={`/merchant/lifecycle/${application.id}`} variant="outlined">View Lifecycle</Button>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApplicationDetail; 